import {
  ExclamationTriangleIcon,
  MicrophoneIcon,
  PaperAirplaneIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  StopIcon,
} from '@heroicons/react/24/outline'

import { VoiceAssistant } from './useVoiceAssistant'

/**
 * The visible half of "Hey Nexys". Three pieces, deliberately kept apart: the header switch that
 * arms wake listening, the composer button for push-to-talk, and the status strip that shows what
 * is being heard. An always-on microphone has to be legible at a glance - if the user cannot tell
 * whether the app is listening, the feature is a liability rather than a convenience.
 */

interface VoiceProps {
  voice: VoiceAssistant
  /** Whether the panel is open - controls tab order the same way the rest of the dock does. */
  open?: boolean
}

/** Wake toggle and reply-aloud toggle, sat in the panel header next to the close button. */
export function VoiceHeaderControls({ voice, open = true }: VoiceProps) {
  if (!voice.available) return null

  // Switched on but not actually listening yet: the browser is waiting for an interaction on this
  // freshly loaded page. Shown as its own state, because a pulsing dot that means "maybe" is worse
  // than no dot at all - the indicator is the only way to know whether the mic is open.
  const pending = voice.pendingGesture
  // Deliberately quiet while the user is in another app: the mic is released so meeting audio
  // is not fought over, and it comes back on its own when this window is focused again.
  const suspended = voice.suspended
  const armed = voice.wakeEnabled && !pending && !suspended && voice.state !== 'denied'

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        tabIndex={open ? 0 : -1}
        aria-pressed={voice.wakeEnabled && voice.state !== 'denied'}
        onClick={() => voice.setWakeEnabled(!voice.wakeEnabled)}
        title={
          suspended
            ? `"${voice.wakePhrase}" is on, but paused while you're in another app - the microphone is ` +
              `released so it can't interfere with meetings. It resumes when you come back.`
            : pending
                ? `"${voice.wakePhrase}" is on. Click anywhere to resume listening.`
                : armed
                    ? `Listening for "${voice.wakePhrase}". Click to stop listening.`
                    : `Listen for "${voice.wakePhrase}" and answer out loud`
        }
        className={`flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-semibold transition-colors ${
          armed ? 'bg-white text-shade-blue' : 'text-white/80 hover:bg-white/20 hover:text-white'
        }`}
      >
        <span className="relative flex h-2 w-2">
          {armed && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-shade-blue opacity-75" />
          )}
          <span
            className={`relative inline-flex h-2 w-2 rounded-full ${
              armed ? 'bg-shade-blue' : pending || suspended ? 'bg-white/50 ring-1 ring-white' : 'bg-white/50'
            }`}
          />
        </span>
        {voice.wakePhrase}
      </button>
      {voice.outputSupported && (
        <button
          type="button"
          tabIndex={open ? 0 : -1}
          aria-pressed={voice.speakReplies}
          onClick={() => voice.setSpeakReplies(!voice.speakReplies)}
          title={voice.speakReplies ? 'Answers are read aloud' : 'Answers stay silent'}
          aria-label={voice.speakReplies ? 'Mute spoken answers' : 'Read answers aloud'}
          className="rounded-md p-1 text-white/80 transition-colors hover:bg-white/20 hover:text-white"
        >
          {voice.speakReplies ? (
            <SpeakerWaveIcon className="h-4 w-4" />
          ) : (
            <SpeakerXMarkIcon className="h-4 w-4" />
          )}
        </button>
      )}
    </div>
  )
}

/** Push-to-talk, for the times saying the wake phrase out loud in an office is not the move. */
export function VoiceMicButton({ voice, disabled }: VoiceProps & { disabled?: boolean }) {
  if (!voice.available) return null

  const capturing = voice.state === 'listening'

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => (capturing ? voice.submitNow() : voice.startListening())}
      title={capturing ? 'Stop and send' : 'Speak your message (Ctrl+Shift+V)'}
      aria-label={capturing ? 'Stop recording and send' : 'Speak your message'}
      aria-pressed={capturing}
      className={`m-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        capturing
          ? 'bg-red-500 text-white hover:bg-red-600'
          : 'text-gray-500 hover:bg-gray-100 hover:text-shade-blue'
      }`}
    >
      <MicrophoneIcon className={`h-4 w-4 ${capturing ? 'animate-pulse' : ''}`} />
    </button>
  )
}

const STATE_LABEL: Record<string, string> = {
  listening: 'Listening…',
  thinking: 'Working on it…',
  speaking: 'Speaking…',
}

/**
 * Sits between the thread and the composer while a voice turn is in flight: what was heard, how
 * far along it is, and a way out. The transcript is shown live rather than only on submit so a
 * misheard loan number is caught before it becomes a wrong answer.
 */
export function VoiceStatusBar({ voice }: VoiceProps) {
  if (!voice.available) return null

  const active = voice.state === 'listening' || voice.state === 'thinking' || voice.state === 'speaking'
  if (!active && !voice.notice) return null

  const denied = voice.state === 'denied'

  return (
    <div
      role="status"
      aria-live="polite"
      className={`shrink-0 border-t px-3 py-2 ${
        denied ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-shade-blue/5'
      }`}
    >
      <div className="flex items-center gap-2">
        {denied ? (
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-500" />
        ) : (
          <VoiceLevel state={voice.state} />
        )}
        <span className={`text-[11px] font-semibold ${denied ? 'text-amber-700' : 'text-shade-blue'}`}>
          {STATE_LABEL[voice.state] || ''}
        </span>
        <span className="min-w-0 flex-1 truncate text-xs text-gray-600" title={voice.transcript || voice.notice}>
          {voice.transcript || voice.notice}
        </span>
        {voice.state === 'listening' && !!voice.transcript && (
          <button
            type="button"
            onClick={voice.submitNow}
            title="Send now"
            aria-label="Send now"
            className="shrink-0 rounded-md p-1 text-shade-blue transition-colors hover:bg-shade-blue/10"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
          </button>
        )}
        {active && (
          <button
            type="button"
            onClick={voice.cancel}
            title={voice.state === 'speaking' ? 'Stop speaking (Esc)' : 'Cancel (Esc)'}
            aria-label={voice.state === 'speaking' ? 'Stop speaking' : 'Cancel'}
            className="shrink-0 rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <StopIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

/** Three bars that move while the mic is open, so "listening" is not a claim taken on faith. */
function VoiceLevel({ state }: { state: string }) {
  const animate = state === 'listening' || state === 'speaking'
  return (
    <span className="flex h-4 shrink-0 items-end gap-0.5" aria-hidden="true">
      {[0, 1, 2].map((bar) => (
        <span
          key={bar}
          className={`w-0.5 rounded-full bg-shade-blue ${animate ? 'animate-bounce' : ''}`}
          style={{
            height: `${[8, 14, 10][bar]}px`,
            animationDelay: `${bar * 0.12}s`,
            animationDuration: '0.9s',
          }}
        />
      ))}
    </span>
  )
}

/**
 * The launcher button's halo. Wake opens the panel, so this mostly matters for the moment before
 * it slides in - and for the case where the user closed the panel with wake still armed.
 */
export function VoiceLauncherHalo({ voice }: VoiceProps) {
  if (!voice.available || !voice.micLive) return null
  return (
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 rounded-full ring-2 ring-white ${
        voice.state === 'listening' ? 'animate-ping' : ''
      }`}
    />
  )
}
